import { useFabriqStore } from "@/store/fabriq";
import { defineStore } from "pinia";
import { RecentSearch, OpenedEntitiesTypes, OpenedEntity } from "@/types";
import { UserService } from "@/services/user";
import storage from "@/utils/storage";
import { formatISO, format } from "date-fns";
import { sortOpenedEntities } from "@/utils/opened-entities";
import { UserConfig } from "@/types";

interface ConfigState {
  config: UserConfig | null;
  configChanged: boolean;
}

export const useConfigStore = defineStore("config", {
  state: (): ConfigState => {
    return {
      config: null,
      configChanged: false,
    };
  },
  actions: {
    setConfig(config: UserConfig) {
      this.config = config;
    },
    async addOpenedTicket(id: number, title: string) {
      const config = { ...(this.config || {}) };
      const openedTickets = config.openedTickets || [];
      const lastOpenedAt = format(new Date(), "yyyy-MM-dd");
      const timestamp = Math.round(Date.now());
      const newOpenedTickets = openedTickets.filter(
        (o: OpenedEntity) => o.ticketId !== id
      );
      newOpenedTickets.push({
        ticketId: id,
        title,
        timestamp,
        lastOpenedAt,
        type: OpenedEntitiesTypes.Ticket,
      });

      this.saveOpenedTickets(newOpenedTickets);
    },

    async addOpenedDocument(id: string, title: string) {
      const config = { ...(this.config || {}) };
      const openedDocuments = config.openedDocuments || [];
      const lastOpenedAt = format(new Date(), "yyyy-MM-dd");
      const timestamp = Math.round(Date.now());

      const newOpenedDocuments = openedDocuments.filter(
        (o: OpenedEntity) => o.objectID !== id
      );
      newOpenedDocuments.push({
        objectID: id,
        title,
        timestamp,
        lastOpenedAt,
        type: OpenedEntitiesTypes.Document,
      });

      this.saveOpenedDocuments(newOpenedDocuments);
    },

    async removeOpenedTicket(id: number) {
      const config = { ...(this.config || {}) };
      let openedTickets = [...(config.openedTickets || [])];
      const idx = openedTickets.findIndex(
        (o: OpenedEntity) => o.ticketId === id
      );
      if (idx < 0) return;
      openedTickets = [
        ...openedTickets.slice(0, idx),
        ...openedTickets.slice(idx + 1),
      ];
      this.saveOpenedTickets(openedTickets);
    },

    async saveOpenedDocuments(openedDocuments: any) {
      const config = { ...(this.config || {}) };
      openedDocuments.sort(sortOpenedEntities);
      openedDocuments = openedDocuments.slice(0, 10);
      this.configChanged = true;
      this.config = { ...config, openedDocuments };
      this.saveUserConfig();
    },

    async saveOpenedTickets(openedTickets: any) {
      const config = { ...(this.config || {}) };
      openedTickets.sort(sortOpenedEntities);
      openedTickets = openedTickets.slice(0, 10);

      this.configChanged = true;
      this.config = { ...config, openedTickets };
      this.saveUserConfig();
    },
    async saveRecentSearch(query: string) {
      const str = query.trim().toLocaleLowerCase();
      const config = { ...(this.config || {}) };
      const recentSearches = config.recentSearches || [];

      const newRecentSearches = recentSearches.filter(
        (o: RecentSearch) => o.query !== query
      );
      newRecentSearches.push({
        query: str,
        createdAt: formatISO(new Date()),
      });

      newRecentSearches.sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      this.configChanged = true;
      this.config = {
        ...config,
        recentSearches: newRecentSearches.slice(0, 10),
      };
      this.saveUserConfig();
    },
    setHasNotifications(hasPushNotifications: boolean) {
      const config = { ...(this.config || {}) };
      if (config.hasPushNotifications === hasPushNotifications) return;
      this.configChanged = true;
      this.config = { ...config, hasPushNotifications };
      this.saveUserConfig();
    },
    async addFavoriteTemplate(id: number) {
      const config = { ...(this.config || {}) };
      const favoriteTemplates = [...(config.favoriteTemplates || [])];
      if (favoriteTemplates.includes(id)) return;
      favoriteTemplates.push(id);
      this.configChanged = true;
      this.config = { ...config, favoriteTemplates };
      this.saveUserConfig();
    },
    async removeFavoriteTemplate(id: number) {
      const config = { ...(this.config || {}) };
      const oldFavoritesTemplates = [...(config.favoriteTemplates || [])];
      if (!oldFavoritesTemplates.includes(id)) return;
      const favoriteTemplates = oldFavoritesTemplates.filter(
        (tid) => tid !== id
      );
      this.configChanged = true;
      this.config = { ...config, favoriteTemplates };
      this.saveUserConfig();
    },
    async addFavoriteEventType(id: string) {
      const config = { ...(this.config || {}) };
      const favoriteEventTypes = [...(config.favoriteEventTypes || [])];
      if (favoriteEventTypes.includes(id)) return;
      favoriteEventTypes.push(id);
      this.configChanged = true;
      this.config = { ...config, favoriteEventTypes };
      this.saveUserConfig();
    },
    async removeFavoriteEventType(id: string) {
      const config = { ...(this.config || {}) };
      const oldFavoritesEventTypes = [...(config.favoriteEventTypes || [])];
      if (!oldFavoritesEventTypes.includes(id)) return;
      const favoriteEventTypes = oldFavoritesEventTypes.filter(
        (tid) => tid !== id
      );
      this.configChanged = true;
      this.config = { ...config, favoriteEventTypes };
      this.saveUserConfig();
    },
    async addFavoriteRoutine(id: number) {
      const config = { ...(this.config || {}) };
      const favoriteRoutines = [...(config.favoriteRoutines || [])];
      if (favoriteRoutines.includes(id)) return;
      favoriteRoutines.push(id);
      this.configChanged = true;
      this.config = { ...config, favoriteRoutines };
      this.saveUserConfig();
    },
    async removeFavoriteRoutine(id: number) {
      const config = { ...(this.config || {}) };
      const oldFavoritesRoutines = [...(config.favoriteRoutines || [])];
      if (!oldFavoritesRoutines.includes(id)) return;
      const favoriteRoutines = oldFavoritesRoutines.filter((tid) => tid !== id);
      this.configChanged = true;
      this.config = { ...config, favoriteRoutines };
      this.saveUserConfig();
    },
    async addFavoriteKpi(id: number) {
      const config = { ...(this.config || {}) };
      const favoriteKpis = [...(config.favoriteKpis || [])];
      if (favoriteKpis.includes(id)) return;
      favoriteKpis.push(id);
      this.configChanged = true;
      this.config = { ...config, favoriteKpis };
      this.saveUserConfig();
    },
    async removeFavoriteKpi(id: number) {
      const config = { ...(this.config || {}) };
      const oldFavoritesKpis = [...(config.favoriteKpis || [])];
      if (!oldFavoritesKpis.includes(id)) return;
      const favoriteKpis = oldFavoritesKpis.filter((tid) => tid !== id);
      this.configChanged = true;
      this.config = { ...config, favoriteKpis };
      this.saveUserConfig();
    },
    async saveUserConfig() {
      // eslint-disable-next-line fabriq-webapp/vue-composable-rules -- we're in a store
      const fabriqStore = useFabriqStore();
      if (!fabriqStore.user) return;
      if (!this.configChanged) return;
      if (!this.config) return;
      try {
        if (fabriqStore.online) {
          const user = await UserService.saveConfig(this.config);
          storage.writeToStorage("user", user);
          this.configChanged = false;
        }
      } catch (e) {
        console.error(e);
      }
    },
  },
});
